import React, { useEffect, useState } from "react";
import { SEOHead } from '../SEOHead';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Lock, User } from "lucide-react";
import { toast } from "sonner@2.0.3";
import { authService } from "../../services/Auth";

interface AdminLoginPageProps {
  onLogin: () => void;
}

export function AdminLoginPage({ onLogin }: AdminLoginPageProps) {
  const [credentials, setCredentials] = useState({
    userId: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("admin_session_expired")) {
      sessionStorage.removeItem("admin_session_expired");
      toast.error("Your session has expired. Please log in again.");
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await authService.login({
        login: credentials.userId,
        password: credentials.password,
      });

      if (data.access_token) {
        toast.success("Login successful! Welcome to Admin Panel.");
        localStorage.setItem("admin_access_token", data.access_token);
        localStorage.setItem("admin_refresh_token", data.refresh_token);
        onLogin();
      } else {
        toast.error("Invalid credentials. Please try again.");
      }
    } catch (error: any) {
      toast.error(error.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <SEOHead
        title="Admin - USP Consultancy"
        description="Admin panel for USP Consultancy. Restricted access."
        canonical="https://uspconsultancy.com.np/admin"
        robots="noindex, nofollow"
      />
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="mb-2">Admin Panel</h1>
          <p className="text-gray-600">USP Consultancy Management System</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Sign In to Continue</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="userId">User ID</Label>
                <div className="relative mt-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    id="userId"
                    name="userId"
                    type="text"
                    value={credentials.userId}
                    onChange={handleInputChange}
                    className="pl-10"
                    placeholder="Enter user ID"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={credentials.password}
                    onChange={handleInputChange}
                    className="pl-10"
                    placeholder="Enter password"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full cursor-pointer"
                disabled={loading}
              >
                {loading ? "Signing In..." : "Sign In"}
              </Button>
            </form>


          </CardContent>
        </Card>
      </div>
    </div>
  );
}
